// Who sees AI Credits. While Credits are only observed, AI Credits is an AEC administration tool; everyone else
// (members and organization administrators) sees their Credits only once blocking is switched on.
import { useQuery } from "@apollo/client/react";
import { GetCreditStatus, MyCreditIdentity } from "@/gql/assistant/documents/AiCredits/MyCreditStatus.document";
import { useCurrentUserRoles } from "@/features/users/hooks/useCurrentUserRoles";

/** True when this viewer should see AI Credits: AEC administrators always, others only while blocking is enabled. */
export const canSeeAiCredits = (isGlobalAdmin: boolean, blockingEnabled: boolean): boolean => isGlobalAdmin || blockingEnabled;

export interface AiCreditsAccess {
  visible: boolean;
  isGlobalAdmin: boolean;
  loading: boolean;
}

/**
 * Whether to show AI Credits entry points. Non-administrators learn the mode from their own Credit status, which is the
 * same cached query the AI Credits page uses, so it costs one request per session.
 */
export function useAiCreditsAccess(options?: { enabled?: boolean }): AiCreditsAccess {
  const enabled = options?.enabled ?? true;
  const { isGlobalAdmin, loading: rolesLoading } = useCurrentUserRoles({ enabled });
  const needsMode = enabled && !rolesLoading && !isGlobalAdmin;
  const identity = useQuery(MyCreditIdentity, { fetchPolicy: "cache-first", skip: !needsMode });
  const userId = identity.data?.me.profile.id;
  const status = useQuery(GetCreditStatus, { variables: { userId: userId ?? "" }, fetchPolicy: "cache-first", skip: !needsMode || !userId });
  const blockingEnabled = status.data?.creditStatus.quotaBlockingEnabled ?? false;

  return {
    visible: canSeeAiCredits(isGlobalAdmin, blockingEnabled),
    isGlobalAdmin,
    loading: rolesLoading || (needsMode && (identity.loading || status.loading)),
  };
}
